import React from 'react';
import { Controller, useWatch } from 'react-hook-form';
import type {
  Control,
  UseFormRegister,
  FieldErrors,
  UseFormSetValue,
} from 'react-hook-form';
import { VirtualizedFormSelect, Alert } from '@roger/ui-components';
import { AlertCircle, AlertTriangle } from 'lucide-react';
import { Field, DateField } from '../../components/FormFields';
import { ClinicianSelect } from '../../components/clinician/ClinicianSelect';
import type { AvailableClinicianResponse } from '../../services/api/types.gen';
import type { SchedulingStepFormData } from '../../schemas/schedulingStepFormSchema';
import { IntakeValidationErrorList } from '../../components/intake/IntakeValidationErrorList';
import { formErrorsToValidationItems } from '../../components/intake/intakeValidationErrorItems';
import { RecOnboardingSummaryLines } from '../../components/intake/RecOnboardingSummaryLines';
import {
  buildEpisodeStartChainPatch,
  buildRecertChainPatch,
  daysBetweenISODates,
  getRecDerivedDates,
} from '../../utils/recSchedulingDates';

interface RecSchedulingStepFormContentProps {
  control: Control<SchedulingStepFormData>;
  register: UseFormRegister<SchedulingStepFormData>;
  setValue: UseFormSetValue<SchedulingStepFormData>;
  errors: FieldErrors<SchedulingStepFormData>;
  handleFieldBlur: (fieldEvent: React.FocusEvent<HTMLElement>) => void;
  saveToServer: (data: Partial<SchedulingStepFormData>) => void;
  rogerClinicians: AvailableClinicianResponse[];
  loadingRogerClinicians: boolean;
  /** Scroll to a field when the user clicks a validation link. */
  onScrollToField?: (fieldName: string) => void;
}

type RecDateKey = 'recert_date' | 'episode_start_date' | 'soc_date';

/**
 * "Schedule REC in Roger" — recert onboarding for a NEW patient migrated into
 * Roger mid-admission. On push the backend creates a back-dated Admission (A),
 * a previous episode [N-60, N-1], an upcoming episode [N, N+59], and an OASIS
 * Recertification visit dated R on the previous episode.
 *
 * Date defaults (R = tomorrow → N = R+1 → A = N-60) are stamped when the
 * "Schedule REC in Roger" checkbox is selected (getRogerNewPatientSchedulePatch),
 * not here — a mount effect would re-fire on tab-switch remounts and intake
 * switches, overwriting user-corrected dates. The auto-chain on edits follows
 * only while the downstream values are still chain-consistent (see
 * buildRecertChainPatch), which likewise needs no mount-scoped ref state.
 */
export const RecSchedulingStepFormContent: React.FC<
  RecSchedulingStepFormContentProps
> = ({
  control,
  register,
  setValue,
  errors,
  handleFieldBlur,
  saveToServer,
  rogerClinicians,
  loadingRogerClinicians,
  onScrollToField,
}) => {
  const recertDate = useWatch({ control, name: 'recert_date' });
  const episodeStartDate = useWatch({ control, name: 'episode_start_date' });
  const socDate = useWatch({ control, name: 'soc_date' });

  /** Push chain-derived keys into the form (the changed key was already set by RHF). */
  const applyDerived = (
    patch: Partial<Record<RecDateKey, string | null>>,
    changedKey: RecDateKey
  ) => {
    (Object.keys(patch) as RecDateKey[]).forEach(key => {
      const value = patch[key];
      if (key === changedKey || value === undefined) return;
      setValue(key, value, { shouldDirty: true, shouldValidate: true });
    });
    saveToServer(patch);
  };

  const handleRecertDateChange = (val: string | null | undefined) => {
    applyDerived(
      buildRecertChainPatch(val, { recertDate, episodeStartDate, socDate }),
      'recert_date'
    );
  };

  const handleEpisodeStartChange = (val: string | null | undefined) => {
    applyDerived(
      buildEpisodeStartChainPatch(val, { episodeStartDate, socDate }),
      'episode_start_date'
    );
  };

  const derived = getRecDerivedDates(episodeStartDate);
  const admissionGapDays = daysBetweenISODates(socDate, episodeStartDate);
  const showGapWarning =
    admissionGapDays !== null && admissionGapDays % 60 !== 0;

  const yearRange = {
    fromYear: new Date().getFullYear() - 3,
    toYear: new Date().getFullYear() + 1,
  };

  return (
    <div className='flex flex-col'>
      {Object.keys(errors).length > 0 && (
        <Alert className='mb-3 py-2 px-3 bg-red-50 border-red-100 text-red-900 text-xs'>
          <div className='flex items-start gap-2'>
            <AlertCircle className='h-4 w-4 mt-0.5 flex-shrink-0 text-red-600' />
            <div className='flex-1 text-red-800'>
              <IntakeValidationErrorList
                title='Missing fields required to schedule:'
                ariaLabel='Validation errors'
                items={formErrorsToValidationItems(errors, onScrollToField, {
                  // Rule-violation messages are too long for this list; the
                  // full reason renders inline under each field.
                  recert_date: 'Recertification Date',
                  soc_date: 'Original Admission/SOC Date',
                })}
              />
            </div>
          </div>
        </Alert>
      )}

      <form className='space-y-5' onSubmit={e => e.preventDefault()}>
        <div className='grid grid-cols-3 gap-3'>
          <ClinicianSelect
            label='Clinician'
            placeholder='Select clinician...'
            clinicians={rogerClinicians}
            role='clinician'
            required={true}
            disabled={loadingRogerClinicians}
            error={errors.roger_clinician_id?.message}
            {...register('roger_clinician_id', {
              onBlur: handleFieldBlur,
            })}
          />

          <ClinicianSelect
            label='Case Manager'
            placeholder='Select case manager...'
            clinicians={rogerClinicians}
            role='caseManager'
            required={true}
            disabled={loadingRogerClinicians}
            error={errors.roger_case_manager_id?.message}
            {...register('roger_case_manager_id', {
              onBlur: handleFieldBlur,
            })}
          />

          <VirtualizedFormSelect
            label='Type of Recertification'
            placeholder='Select type...'
            options={[
              { value: 'SN', label: 'Nursing' },
              { value: 'PT', label: 'Physical Therapy' },
              { value: 'ST', label: 'Speech Therapy' },
              { value: 'OT', label: 'Occupational Therapy' },
            ]}
            required={true}
            disabled={loadingRogerClinicians}
            error={errors.roger_type_of_soc?.message}
            {...register('roger_type_of_soc', {
              onBlur: handleFieldBlur,
            })}
          />
        </div>

        <div className='grid grid-cols-3 gap-3'>
          <Controller
            name='recert_date'
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <DateField
                fieldAnchor='recert_date'
                label='Recertification Date'
                value={value}
                htmlFor='rogerRecertDate'
                required={true}
                disabled={false}
                error={errors.recert_date?.message}
                onChange={val => {
                  onChange(val);
                  handleRecertDateChange(val);
                }}
                onBlur={onBlur}
                yearRange={yearRange}
              />
            )}
          />

          <Controller
            name='episode_start_date'
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <DateField
                fieldAnchor='episode_start_date'
                label='New Episode Start Date'
                value={value}
                htmlFor='rogerRecEpisodeStartDate'
                required={true}
                disabled={false}
                error={errors.episode_start_date?.message}
                onChange={val => {
                  onChange(val);
                  handleEpisodeStartChange(val);
                }}
                onBlur={onBlur}
                yearRange={yearRange}
              />
            )}
          />

          <Field
            label='Original MRN'
            htmlFor='rogerRecOriginalMrn'
            required={true}
            error={errors.kinnser_mrn?.message}
            inputProps={{
              ...register('kinnser_mrn', {
                onBlur: handleFieldBlur,
              }),
            }}
          />
        </div>

        <div className='grid grid-cols-3 gap-3'>
          <Controller
            name='soc_date'
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <DateField
                fieldAnchor='soc_date'
                label='Original Admission/SOC Date'
                value={value}
                htmlFor='rogerRecAdmissionDate'
                required={true}
                disabled={false}
                error={errors.soc_date?.message}
                onChange={val => {
                  onChange(val);
                  saveToServer({ soc_date: val });
                }}
                onBlur={onBlur}
                yearRange={yearRange}
              />
            )}
          />
        </div>

        {showGapWarning && (
          <Alert className='py-2 px-3 bg-amber-50 border-amber-200'>
            <div className='flex items-start gap-2'>
              <AlertTriangle className='h-4 w-4 mt-0.5 flex-shrink-0 text-amber-600' />
              <span className='text-amber-900 text-xs'>
                The gap between the admission date and the new episode start is{' '}
                {admissionGapDays} days, not a multiple of 60. Admissions are
                usually a chain of consecutive 60-day episodes — double-check
                the Original Admission/SOC Date.
              </span>
            </div>
          </Alert>
        )}

        {derived && (
          <div className='rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 space-y-1'>
            <RecOnboardingSummaryLines
              episodeStartDate={episodeStartDate}
              admissionDate={socDate}
              recertDate={recertDate}
            />
          </div>
        )}
      </form>
    </div>
  );
};
